import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { AgencyCreatePayload } from "../../../../Schema/agencyCreate.types";
import { agencyListActions } from "../agencyList/agencyList";

export type CreateAgencyLoadAction = {
  type: string;
  payload: AgencyCreatePayload;
};

export type CreateAgencyData = {
  data: {
    message?: string;
    status?: string;
    agency?: { username: string };
  };
};

const CreateAgency = new ReduxFetchState<
  CreateAgencyData | null,
  AgencyCreatePayload,
  any
>("createAgency");

const RESET_CREATE_AGENCY = "RESET_CREATE_AGENCY";

/**
 * Builds the multipart body. Arrays / nested objects use the bracket notation
 * (links[0], services[0][title], ...) that Laravel parses natively.
 * Empty optional values are skipped instead of being sent as "".
 */
export const buildAgencyFormData = (payload: AgencyCreatePayload) => {
  const formData = new FormData();

  const appendIfFilled = (key: string, value?: string | null) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  };

  // فیلدهای ساده
  formData.append("username", payload.username);
  formData.append("financial_cal_side", payload.financial_cal_side);
  formData.append("name", payload.name);
  formData.append("color", payload.color);

  appendIfFilled("link", payload.link);
  appendIfFilled("slogan", payload.slogan);
  appendIfFilled("bio", payload.bio);

  if (payload.logo) {
    formData.append("logo", payload.logo);
  }

  // contacts: phone و email به آرایه‌ی contacts منتقل شدن
  const contacts: { type: string; value: string }[] = [];
  if (payload.phone) contacts.push({ type: "cellphone", value: payload.phone });
  if (payload.email) contacts.push({ type: "email", value: payload.email });

  contacts.forEach((contact, index) => {
    formData.append(`contacts[${index}][type]`, contact.type);
    formData.append(`contacts[${index}][value]`, contact.value);
  });

  // links: حالا آرایه‌ای از آبجکت
  payload.links
      .filter(Boolean)
      .forEach((link, index) => {
        formData.append(`links[${index}][link]`, link);
      });

  // services: link اختیاریه
  payload.services.forEach((service, index) => {
    formData.append(`services[${index}][title]`, service.title);
    appendIfFilled(`services[${index}][link]`, service.link);
  });

  // social_medias
  payload.social_medias.forEach((item, index) => {
    formData.append(`social_medias[${index}][social_media]`, item.social_media);
    formData.append(`social_medias[${index}][social_media_link]`, item.social_media_link);
  });

  return formData;
};

export function* watchCreateAgency(action: CreateAgencyLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: CreateAgencyData = yield axios({
      url: "/api/v1/ai/agencies",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
        lang,
      },
      data: buildAgencyFormData(action.payload),
    });

    yield alert({
      message: data.data.message ?? "دستیار شما با موفقیت ساخته شد.",
      type: "success",
    });
    yield put(CreateAgency.actions.loadSuccess({ data: { ...data.data, status: "success" } }));
    yield put(agencyListActions.load());
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? e?.response?.data?.message ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(CreateAgency.actions.loadFailure(e.message));
  }
}

export function* createAgencySagas() {
  yield takeEvery(CreateAgency.actionTypes.load, watchCreateAgency);
}

const initialState = CreateAgency.reducer(undefined, { type: "@@INIT" });

const customReducer = (state = initialState, action: any) => {
  if (action.type === RESET_CREATE_AGENCY) {
    return initialState;
  }
  return CreateAgency.reducer(state, action);
};

export function useCreateAgency() {
  const { loading, data, ...createAgencyState } = useAppSelector((state) => state.createAgency);
  const dispatch = useAppDispatch();

  const dispatchCreateAgency = useCallback(
    (payload: AgencyCreatePayload) => {
      dispatch(CreateAgency.actions.load(payload));
    },
    [dispatch],
  );

  const clearCreateAgency = useCallback(() => {
    dispatch({ type: RESET_CREATE_AGENCY });
  }, [dispatch]);

  return {
    ...createAgencyState,
    dispatchCreateAgency,
    clearCreateAgency,
    createAgencyLoading: loading,
    createAgencyData: data,
  };
}

export const {
  actionTypes: createAgencyActionsTypes,
  actions: createAgencyActions,
} = CreateAgency;

export const createAgencyReducer = customReducer;
